import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import StaffReservation from '../reservations/StaffReservation';
import {
    cancelReservation,
    startReservation,
    timeoutReservation,
} from '../../lib/reservationApi';

// The existing conversation action opens the shared reservation form.
// Pending reviews use the configured visible timer; saved trips reopen in Dispatch.

const NOTICE_MS = 9000;

// BL-177 Step 2n: `menuItem` = the button lives inside the phone "⋯" menu. It then reads as a menu row, and its error line, review tile and notice
// are rendered on the page (not inside the menu), so closing the menu never hides or loses an open reservation.
export default function CreateReservationButton({ client, sessionId, disabled = false, disabledReason = '', onChanged, userId, accounts: initialAccounts=[], className = 'ia-btn3d ia-comms-pill', menuItem = false }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [tile, setTile] = useState(null); // { reservation, countdown, complete }
    const [accounts,setAccounts]=useState(initialAccounts);
    const [tileBusy, setTileBusy] = useState(false);
    const [notice, setNotice] = useState(null); // { text, link, linkLabel }
    const noticeTimer = useRef(null);
    const alive = useRef(true);
    useEffect(()=>{if(!tile||!client?.from)return undefined;let active=true;(async()=>{try{const {data,error}=await client.from('accounts').select('id,account_name').order('account_name');if(active&&!error&&Array.isArray(data))setAccounts(data);}catch{/* Keep the thread account if a broader picker is unavailable. */}})();return()=>{active=false;};},[client,tile]);

    useEffect(() => () => { alive.current = false; clearTimeout(noticeTimer.current); }, []);

    const showNotice = useCallback((next) => {
        setNotice(next);
        clearTimeout(noticeTimer.current);
        noticeTimer.current = setTimeout(() => { if (alive.current) setNotice(null); }, NOTICE_MS);
    }, []);

    const finish = useCallback((response) => {
        const reservation = response.reservation;
        const number = reservation?.confirmationNumber;
        if (response.state === 'created') {
            setTile(null);
            showNotice({
                text: `Reservation #${number} created and on the dispatch list.${response.confirmationSent ? ' The client has been sent their confirmation.' : ''}`,
                link: reservation?.link, linkLabel: 'Open on the dispatch list',
            });
        } else if (response.state === 'needs_info') {
            setTile(null);
            showNotice({
                text: `Reservation #${number} added to dispatch as Needs info. The client confirmation is on hold until the missing details are completed.`,
                link: reservation?.link, linkLabel: 'Open on the dispatch list',
            });
        } else if (response.state === 'canceled') {
            setTile(null);
        }
        onChanged?.(response);
    }, [onChanged, showNotice]);

    const press = useCallback(async () => {
        if (busy || disabled || !sessionId) return;
        setBusy(true);
        setError('');
        try {
            const response = await startReservation(client, sessionId);
            if (!alive.current) return;
            if (response.state === 'review') {
                setTile({ reservation: response.reservation, countdown: true, complete: false });
            } else if (response.state === 'existing') {
                const reservation = response.reservation;
                if (reservation.status === 'pending_review')setTile({reservation,countdown:false,complete:false});
                else showNotice({text:`Reservation #${reservation.confirmationNumber} already exists. Open it on the dispatch list to continue.`,link:reservation.link,linkLabel:'Open on the dispatch list'});
            } else {
                finish(response);
            }
        } catch (failure) {
            if (alive.current) setError(failure.message || 'The reservation could not be created.');
        } finally {
            if (alive.current) setBusy(false);
        }
    }, [busy, client, disabled, finish, sessionId, showNotice]);

    const timeOut = useCallback(async () => {
        if (!tile) return;
        setTileBusy(true);
        try {
            const response = await timeoutReservation(client, tile.reservation.id);
            if (alive.current) finish(response);
        } catch (failure) {
            if (alive.current) {
                setTile(null);
                setError(failure.message || 'The reservation could not be added to dispatch.');
            }
        } finally {
            if (alive.current) setTileBusy(false);
        }
    }, [client, finish, tile]);

    const cancel = useCallback(async () => {
        if (!tile) return;
        // an unfinished Needs info trip stays where it is; Cancel only closes the tile. A pending request is withdrawn.
        if (tile.complete) { setTile(null); return; }
        setTileBusy(true);
        try {
            const response = await cancelReservation(client, tile.reservation.id);
            if (alive.current) finish(response);
        } catch (failure) {
            if (alive.current) setError(failure.message || 'Could not cancel.');
        } finally {
            if (alive.current) setTileBusy(false);
        }
    }, [client, finish, tile]);

    const overlays = (
        <>
            {error && <span role="alert" className="text-xs font-semibold text-red-600" data-testid="create-reservation-error">{error}</span>}
            {tile && (
                <StaffReservation
                    key={tile.reservation.id} disabled={tileBusy} client={client} accounts={accounts} accountId={tile.reservation.accountId || initialAccounts[0]?.id || ''}
                    sourceReservation={tile.reservation} initialFields={tile.reservation.fields} userId={userId}
                    initiallyOpen hideTrigger countdownSeconds={tile.reservation.countdownSeconds||30}
                    onTimeout={tile.countdown?timeOut:undefined} onClose={()=>setTile(null)} onCancel={cancel}
                    onChanged={(result)=>{setTile(null);showNotice({text:`Reservation #${result.confirmationNumber} requested. Staff confirms the details and price.`,link:`/dispatch?engagement=${result.engagementId}`,linkLabel:'Open on the dispatch list'});onChanged?.(result);}}
                />
            )}
            {notice && (
                <div className="ia-d2 ia-popup3d ia-resv-notice" role="status" aria-live="polite" data-testid="reservation-notice">
                    <p className="text-sm font-bold text-white">{notice.text}</p>
                    {notice.link && <p className="mt-1 text-sm"><a href={notice.link}>{notice.linkLabel}</a></p>}
                </div>
            )}
        </>
    );

    return (
        <>
            <SupportWriteGuard mode="disable" tooltip="Creating reservations is disabled in a read-only support session">
                <button
                    type="button"
                    className={className}
                    role={menuItem ? 'menuitem' : undefined}
                    onClick={press}
                    disabled={busy || disabled || !sessionId}
                    title={disabled && disabledReason ? disabledReason : 'Read this conversation and create a reservation from it'}
                    aria-busy={busy ? 'true' : undefined}
                    data-testid="create-reservation-button"
                >
                    {busy ? 'Reading…' : 'Create Reservation'}
                </button>
            </SupportWriteGuard>
            {menuItem && typeof document !== 'undefined' ? createPortal(overlays, document.body) : overlays}
        </>
    );
}
